import { useCallback, useEffect, useMemo, useState } from 'react';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	memoizedFetchSiteUserPersonalization,
	type Trait,
} from '@atlassian/jira-personalization-service';
import { FREE_EDITION } from '@atlassian/jira-shared-types';
import {
	useAppEditions,
	useLicensedProducts,
	useTenantContext,
} from '@atlassian/jira-tenant-context-controller';
import { getBillingInformation } from '../../services';

const PACKAGE_NAME = 'jira-business.monetisation-banner-experiment';

export const getStartOfUTCDay = (timestamp: number) => new Date(timestamp).setUTCHours(0, 0, 0, 0);

// Checking whether site is in trial or grace period (14 days after trial ends).
// We can do this for HAMS but not for CCP. CCP has feature called pre-dunning.
// We do not know how to identify pre-dunning since new subscription is created without trialEndDate
// so we basically have to check invoiceability or everyone on CCP.
export const isInTrialOrGracePeriod = (billingSourceSystem: string, trialEndTime: number) =>
	billingSourceSystem === 'HAMS' &&
	trialEndTime &&
	differenceInCalendarDays(new Date(), getStartOfUTCDay(trialEndTime)) < 14;

export const useTrialBannerExperiment = () => {
	const clearCache = useCallback(() => {
		if (fg('jwm_create_project_from_sample')) {
			memoizedFetchSiteUserPersonalization.clear();
		}
	}, []);

	// All of the below functions are only used by `shouldRenderExperiment`, but are
	// placed outside it as they involve React hooks.

	// Licensing
	const licensedProducts = useLicensedProducts();
	const { software: edition } = useAppEditions();

	// Billing
	const { atlassianAccountId, cloudId, isSiteAdmin, productEntitlementDetails } =
		useTenantContext();

	const entitlementDetails = productEntitlementDetails?.['jira-software'];
	// If billingSourceSystem is not set, it is HAMS.
	const billingSourceSystem = entitlementDetails?.billingSourceSystem ?? 'HAMS';
	const { entitlementId } = entitlementDetails || {};
	const inTrialOrGracePeriod = useMemo(
		() =>
			entitlementDetails?.trialEndTime
				? isInTrialOrGracePeriod(billingSourceSystem, entitlementDetails.trialEndTime)
				: false,
		[billingSourceSystem, entitlementDetails?.trialEndTime],
	);
	const inHAMSTrialOrGracePeriod = billingSourceSystem === 'CCP' ? false : inTrialOrGracePeriod;

	const [shouldRenderExperiment, setShouldRenderExperiment] = useState<boolean | null>(null);

	useEffect(() => {
		let cancelled = false;

		const userId = atlassianAccountId ?? '';
		const hasJSWLicence = licensedProducts?.[LICENSED_PRODUCTS.JIRA_SOFTWARE];
		const hasJSWFreeTierLicence = edition === FREE_EDITION;
		const hasExplicitJWMLicence = licensedProducts?.[LICENSED_PRODUCTS?.JIRA_CORE];

		const initialConditions =
			!!cloudId &&
			isSiteAdmin &&
			!!userId &&
			hasJSWLicence &&
			hasJSWFreeTierLicence &&
			!hasExplicitJWMLicence &&
			!inHAMSTrialOrGracePeriod;

		if (!initialConditions) {
			setShouldRenderExperiment(false);
			return;
		}

		const traitName = 'jira_workmanagement_ui_jwmmonetisationbannerdismiss_clicked_last';
		const traitCondition = (trait?: Trait) =>
			trait?.value
				? differenceInCalendarDays(
						new Date(),
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						getStartOfUTCDay((trait?.value as number) ?? 0),
					) > 7
				: true; // if the trait does not exist, user is still eligible for enrolment.

		const checkWhetherBannerDismissed = memoizedFetchSiteUserPersonalization({
			cloudId,
			userId,
		}).then(
			(value) => {
				if (cancelled) {
					return null;
				}

				const trait = value.find(({ name }) => name === traitName);

				return traitCondition(trait);
			},
			() => false,
		);

		const checkWhetherIsBillingAdmin = getBillingInformation({
			cloudId,
			userId,
			billingSourceSystem,
			entitlementId,
			isSiteAdmin,
		}).then(
			(billingInformation) => {
				if (cancelled) {
					return null;
				}

				return billingSourceSystem === 'CCP'
					? billingInformation.isBillingAdmin !== false || billingInformation.invoiceable !== false
					: isSiteAdmin;
			},
			(error) => {
				fireErrorAnalytics({
					meta: {
						id: 'JwmMonetizationTrialBannerExperimentPipeline',
						packageName: PACKAGE_NAME,
						teamName: 'jira-warepil',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});

				return false;
			},
		);

		Promise.all([checkWhetherBannerDismissed, checkWhetherIsBillingAdmin]).then((values) => {
			if (values.some((value) => value == null)) {
				return;
			}

			if (values.every(Boolean)) {
				setShouldRenderExperiment(
					expValEquals('jwm_implicit_user_monetization_experiment', 'cohort', 'variation'),
				);
			} else {
				setShouldRenderExperiment(false);
			}
		});

		// Guarding against race conditions through `cancelled`, when this `useEffect` is
		// being run more than once.
		return () => {
			cancelled = true;
		};
	}, [
		atlassianAccountId,
		billingSourceSystem,
		cloudId,
		edition,
		entitlementId,
		inHAMSTrialOrGracePeriod,
		isSiteAdmin,
		licensedProducts,
	]);

	return { shouldRenderExperiment, clearCache };
};
